/* =========================================================================
   Image editor — crop to shape, rotate, zoom, drag to reposition.

   Ported from iteration 1. Two changes:
     - it is handed and returns a Blob rather than a base64 data URL
     - buttons use data-act instead of inline onclick
   ========================================================================= */

import { openModal, closeModal, toast, registerActions, registerInputActions } from './dom.js'
import { canvasToBlob, loadImage } from '../data/images.js'

const IE_ASPECTS = [['Wide 16:9', 16 / 9], ['Photo 4:3', 4 / 3], ['Square', 1], ['Portrait 4:5', 4 / 5]]

let IE = null

/** Open the OS photo picker, then the editor. cb receives (blob, aspect). */
export function pickImage(cb, aspect) {
  const inp = document.createElement('input')
  inp.type = 'file'
  inp.accept = 'image/*'
  inp.onchange = () => {
    const f = inp.files[0]
    if (!f) return
    openImageEditor(f, aspect || 16 / 9, cb)
  }
  inp.click()
}

export async function openImageEditor(src, aspect, cb) {
  IE = {
    src, aspect, cb, rot: 0, z: 1, minScale: 1, ox: 0, oy: 0,
    img: null, W: 460, H: Math.round(460 / aspect), drag: null,
  }

  openModal(`
    <h3>Adjust photo</h3>
    <div class="iewrap"><canvas id="ieCanvas"></canvas></div>
    <div class="iehint">Drag the photo to reposition · use the slider to zoom in and crop</div>
    <label>Shape</label>
    <div class="chips" id="ieShapes">${IE_ASPECTS.map(([n, a]) =>
      `<button class="chip ${Math.abs(a - aspect) < 0.01 ? 'on' : ''}" data-act="ieAspect" data-a="${a}">${n}</button>`
    ).join('')}</div>
    <div class="ierow">
      <button class="btn small" data-act="ieRotate" data-d="-90">↺ Rotate left</button>
      <button class="btn small" data-act="ieRotate" data-d="90">↻ Rotate right</button>
      <button class="btn small" data-act="ieReset">Reset</button>
    </div>
    <div class="ierow">
      <label>Zoom</label>
      <input class="iezoom" id="ieZoom" type="range" min="1" max="4" step="0.02" value="1" data-input-act="ieZoom">
    </div>
    <div class="actions">
      <button class="btn" data-act="closeModal">Cancel</button>
      <button class="btn primary" data-act="ieSave">Use this photo</button>
    </div>`, () => { IE = null })

  try {
    const img = await loadImage(src)
    if (!IE) return                       // user cancelled while it decoded
    IE.img = img
    ieFit(); ieBindDrag(); ieDraw()
  } catch {
    toast("Couldn't read that image — try a JPEG or PNG")
    closeModal()
  }
}

function ieEff() {                        // effective dims allowing for rotation
  const { img, rot } = IE
  return (rot % 180) ? { w: img.height, h: img.width } : { w: img.width, h: img.height }
}

function ieFit() {
  const e = ieEff()
  IE.minScale = Math.max(IE.W / e.w, IE.H / e.h)   // cover the frame
  IE.ox = 0; IE.oy = 0
  const zi = document.getElementById('ieZoom')
  if (zi) zi.value = IE.z
  ieClamp()
}

function ieScale() { return IE.minScale * IE.z }

function ieClamp() {
  const e = ieEff(), s = ieScale()
  const maxOx = Math.max(0, (e.w * s - IE.W) / 2)
  const maxOy = Math.max(0, (e.h * s - IE.H) / 2)
  IE.ox = Math.max(-maxOx, Math.min(maxOx, IE.ox))
  IE.oy = Math.max(-maxOy, Math.min(maxOy, IE.oy))
}

function ieDraw() {
  const c = document.getElementById('ieCanvas')
  if (!c || !IE || !IE.img) return
  c.width = IE.W; c.height = IE.H
  const ctx = c.getContext('2d')
  ctx.fillStyle = '#1e1e1e'; ctx.fillRect(0, 0, IE.W, IE.H)
  ctx.save()
  ctx.translate(IE.W / 2 + IE.ox, IE.H / 2 + IE.oy)
  ctx.rotate(IE.rot * Math.PI / 180)
  const s = ieScale()
  ctx.scale(s, s)
  ctx.drawImage(IE.img, -IE.img.width / 2, -IE.img.height / 2)
  ctx.restore()
  // rule-of-thirds guides
  ctx.strokeStyle = 'rgba(255,255,255,.28)'; ctx.lineWidth = 1
  for (let i = 1; i < 3; i++) {
    ctx.beginPath(); ctx.moveTo(IE.W * i / 3, 0); ctx.lineTo(IE.W * i / 3, IE.H); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(0, IE.H * i / 3); ctx.lineTo(IE.W, IE.H * i / 3); ctx.stroke()
  }
}

function ieBindDrag() {
  const c = document.getElementById('ieCanvas')
  if (!c) return
  const ratio = () => c.width / c.getBoundingClientRect().width
  c.onpointerdown = ev => {
    c.setPointerCapture(ev.pointerId)
    IE.drag = { x: ev.clientX, y: ev.clientY, ox: IE.ox, oy: IE.oy }
  }
  c.onpointermove = ev => {
    if (!IE || !IE.drag) return
    const k = ratio()
    IE.ox = IE.drag.ox + (ev.clientX - IE.drag.x) * k
    IE.oy = IE.drag.oy + (ev.clientY - IE.drag.y) * k
    ieClamp(); ieDraw()
  }
  c.onpointerup = c.onpointercancel = () => { if (IE) IE.drag = null }
}

async function ieSave() {
  if (!IE || !IE.img) return
  const outW = 1000, outH = Math.round(outW / IE.aspect), f = outW / IE.W
  const c = document.createElement('canvas')
  c.width = outW; c.height = outH
  const ctx = c.getContext('2d')
  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, outW, outH)
  ctx.save()
  ctx.translate(outW / 2 + IE.ox * f, outH / 2 + IE.oy * f)
  ctx.rotate(IE.rot * Math.PI / 180)
  const s = ieScale() * f
  ctx.scale(s, s)
  ctx.drawImage(IE.img, -IE.img.width / 2, -IE.img.height / 2)
  ctx.restore()

  const blob = await canvasToBlob(c, 'image/jpeg', 0.85)
  const cb = IE.cb, aspect = IE.aspect
  closeModal()
  IE = null
  // the aspect travels with the photo so the display frame matches the crop exactly
  cb(blob, aspect)
}

registerActions({
  ieRotate: d => { if (!IE) return; IE.rot = (IE.rot + Number(d.d) + 360) % 360; ieFit(); ieDraw() },
  ieReset: () => { if (!IE) return; IE.rot = 0; IE.z = 1; ieFit(); ieDraw() },
  ieAspect: (d, el) => {
    if (!IE) return
    const a = Number(d.a)
    IE.aspect = a; IE.H = Math.round(IE.W / a)
    ;[...el.parentNode.children].forEach(x => x.classList.remove('on'))
    el.classList.add('on')
    ieFit(); ieDraw()
  },
  ieSave,
})

registerInputActions({
  ieZoom: (d, el) => { if (!IE) return; IE.z = parseFloat(el.value); ieClamp(); ieDraw() },
})
